import {
  Alert,
  Button,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Box,
  Typography,
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import UploadIcon from '@mui/icons-material/Upload';
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import './SaveList.css';
import { Helmet } from "react-helmet-async";

const STORAGE_KEY = (version) => `poeregex${ version ? '2' : ''}`;

const TABS_POE1 = [
  { id: 'map', label: '맵모드' },
  { id: 'ultimatum', label: '결전' },
  { id: 'scarab', label: '갑충석' },
  { id: 'beast', label: '야수' },
  { id: 'voyage', label: '항해' },
];

const TABS_POE2 = [
  { id: 'map', label: '경로석' },
  { id: 'tablet', label: '서판' },
];

// 저장된 result/engResult를 그대로 복원해 해당 페이지 목록 선택 상태까지 불러올 수 있는 타입
const LOADABLE_TYPES = ['map', 'tablet'];

const LOAD_DESTINATION = {
  map: (version) => version ? '/poe2/map' : '/map',
  tablet: () => '/poe2/tablet',
};

function getDisplayRegex(value, viewKor) {
  if (viewKor) return value.regexKor ?? value.regex ?? '';
  return value.regexEng ?? value.regex ?? '';
}

function canLoadToSearch(value) {
  return value && value.result && value.engResult;
}

/** 맵모드는 기존 형식 유지. type 없는 레거시는 result/영한 필드로 맵, 단순 name+regex는 갑충석으로 분류 */
function getSaveType(item) {
  if (item.type === 'ultimatum' || item.type === 'scarab' || item.type === 'beast' || item.type === 'map' || item.type === 'voyage' || item.type === 'tablet') {
    return item.type;
  }
  if (item.result != null || item.regexKor != null || item.regexEng != null) {
    return 'map';
  }
  return 'scarab';
}

function SaveList({ version }) {
  const [saveList, setSaveList] = useState([]);
  const [viewKor, setViewKor] = useState(true);
  const [tab, setTab] = useState('map');
  const navigate = useNavigate();
  const TABS = version ? TABS_POE2 : TABS_POE1;

  useEffect(() => {
    const key = STORAGE_KEY(version);
    if (localStorage.getItem(key))
      setSaveList(JSON.parse(localStorage.getItem(key)));
    setTab('map');
  }, [version])

  const filteredList = useMemo(() => {
    return saveList.filter((item) => getSaveType(item) === tab);
  }, [saveList, tab]);

  const copyClipBoard = async (reg) => {
    try {
      await navigator.clipboard.writeText(reg);
      alert('복사 완료!');
    } catch (error) {
      alert('복사 실패!');
    }
  };

  function deleteList(item) {
    if (!window.confirm('삭제 하시겠습니까?'))
      return;
    const key = STORAGE_KEY(version);
    const itemKey = item.regexKor ?? item.regex;
    const itemType = getSaveType(item);
    let localData = JSON.parse(localStorage.getItem(key));
    // 동일 regex 문자열이 다른 타입에 있을 수 있어 type까지 맞춰 삭제
    let removed = false;
    let filterData = localData.filter((data) => {
      if (removed) return true;
      const sameKey = (data.regexKor ?? data.regex) === itemKey;
      const sameType = getSaveType(data) === itemType;
      if (sameKey && sameType) {
        removed = true;
        return false;
      }
      return true;
    });
    localStorage.setItem(key, JSON.stringify(filterData));
    setSaveList(filterData);
  }

  function clearAllSaves() {
    if (!window.confirm('저장된 목록을 모두 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.'))
      return;
    const key = STORAGE_KEY(version);
    localStorage.removeItem(key);
    setSaveList([]);
  }

  function loadToSearch(item) {
    sessionStorage.setItem('poeregex_load', JSON.stringify({ result: item.result, engResult: item.engResult, rawFields: item.rawFields }));
    const type = getSaveType(item);
    const destination = (LOAD_DESTINATION[type] || LOAD_DESTINATION.map)(version);
    navigate(destination);
  }

  return (
    <>
      <Helmet>
        <title>정규식 저장 목록</title>
        <meta
          name="description"
          content="패스 오브 엑자일(POE) 정규식 저장 목록. 생성한 정규식을 저장하고 관리할 수 있는 페이지입니다. 저장된 정규식을 복사하거나 삭제할 수 있으며, POE1과 POE2 버전을 구분하여 저장합니다."
        />
        <meta property="og:title" content="정규식 저장 목록 - POE Regex KR" />
        <meta property="og:description" content="패스 오브 엑자일(POE) 정규식 저장 목록. 생성한 정규식을 저장하고 관리할 수 있는 페이지입니다." />
        <meta property="og:url" content="https://poeregexkr.web.app/savelist" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://poeregexkr.web.app/mirror.png" />
      </Helmet>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', my: 1}}>
        <Button
          variant="contained"
          color="error"
          onClick={clearAllSaves}
          disabled={saveList.length === 0}
        >
          저장 목록 초기화
        </Button>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          textColor="primary"
          indicatorColor="primary"
        >
          {TABS.map((t) => (
            <Tab
              key={t.id}
              value={t.id}
              label={t.label}
            />
          ))}
        </Tabs>
      </Box>

      {filteredList.length === 0 || filteredList === null ? (
        <Typography variant="h6" color="text.secondary">저장된 정규식이 없습니다....</Typography>
      ) : (
        <>
          {!version && tab === 'map' && (
            <FormControlLabel
              control={
                <Switch
                  checked={!viewKor}
                  onChange={() => setViewKor((k) => !k)}
                  color="primary"
                />
              }
              label={viewKor ? '한글' : '영문'}
              sx={{ mb: 1 }}
              labelPlacement="start"
            />
          )}
          <List sx={{ width: '100%' }}>
            {filteredList.map((value, i) => {
              const displayRegex = tab === 'map'
                ? getDisplayRegex(value, viewKor)
                : (value.regex ?? '');
              const loadable = LOADABLE_TYPES.includes(tab) && canLoadToSearch(value);
              return (
                <ListItem
                  key={`${getSaveType(value)}-${value.name}-${i}`}
                  secondaryAction={
                    <>
                      {LOADABLE_TYPES.includes(tab) && loadable && (
                        <Tooltip title="검색탭에서 불러오기" followCursor>
                          <IconButton onClick={() => loadToSearch(value)} sx={{ mr: 0.5 }}>
                            <UploadIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {LOADABLE_TYPES.includes(tab) && !loadable && (
                        <Tooltip title="이전 형식으로 저장된 항목은 불러오기를 지원하지 않습니다" followCursor>
                          <span>
                            <IconButton disabled sx={{ mr: 0.5 }}>
                              <UploadIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title="복사" followCursor>
                        <IconButton onClick={() => copyClipBoard(displayRegex)}>
                          <ContentCopyIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="삭제" followCursor>
                        <IconButton onClick={() => deleteList(value)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  }
                >
                  <ListItemText className="noScroll" sx={{width: 1/5, whiteSpace: 'nowrap', overflowX:'scroll', mr:'10px'}}>{value.name}</ListItemText>
                  <ListItemText className="noScroll" sx={{width: 4/5, whiteSpace: 'nowrap', overflowX:'scroll', mr:'10%'}} primary={displayRegex} />
                </ListItem>
              );
            })}
          </List>
        </>
      )}
    </>
  );
}

export default SaveList;
